import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import SEO from '../../components/SEO';
import { ExternalLink, Sparkles, Handshake, Calculator, ChevronDown, MapPin, ShieldCheck, MessageCircle, Languages, Scale, Banknote, HeartPulse, Umbrella, Cpu, MoreHorizontal, ArrowRight } from 'lucide-react';
import { trackDiscountClicked, trackEvent } from '../../lib/analytics';
import { useAuth } from '../../context/AuthContext';

/**
 * Mundalo - Partners page (a.k.a. /portal/partners).
 *
 * Iter 81: partners are now sourced from MongoDB via /api/partners (admin-
 * editable). The page structure (header, FTC disclosure, partner cards, and
 * "more coming soon" banner) is unchanged - only the data source moved.
 *
 * Iter 109: SafetyWing-specific enhancement. When the partner is SafetyWing,
 * the card grows a collapsible "Calculate your price" section that lazy-loads
 * SafetyWing's official price-widget script, and the CTA points at our
 * referenceID-tagged affiliate URL. All other partner cards are unchanged.
 *
 * FTC disclosure rendered at the top of the page (required for affiliate
 * links).
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

const FTC_DISCLOSURE =
  'Some links on this page are affiliate links. Mundalo may earn a commission if you purchase through these links, at no extra cost to you.';

// --- SafetyWing widget integration (iter 109) ----------------------------
const SAFETYWING_AFFILIATE_URL =
  'https://safetywing.com/?referenceID=26535148&utm_source=26535148&utm_medium=Ambassador';
const SAFETYWING_WIDGET_SRC =
  'https://storage.googleapis.com/safetywing-static/widget/safetywing-price-widget.js';

const isSafetyWingPartner = (p) =>
  (((p?.id || '') + ' ' + (p?.name || '')).toLowerCase().replace(/\s+/g, ''))
    .includes('safetywing');

// Load the SafetyWing script exactly once across the page, regardless of
// how many SafetyWing cards render (defensive - admins could add duplicates).
let safetyWingScriptPromise = null;
const loadSafetyWingScript = () => {
  if (safetyWingScriptPromise) return safetyWingScriptPromise;
  safetyWingScriptPromise = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') { resolve(); return; }
    const existing = document.querySelector(`script[src="${SAFETYWING_WIDGET_SRC}"]`);
    if (existing) { resolve(); return; }
    const s = document.createElement('script');
    s.src = SAFETYWING_WIDGET_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = (e) => { safetyWingScriptPromise = null; reject(e); };
    document.body.appendChild(s);
  });
  return safetyWingScriptPromise;
};

const SafetyWingPriceCalculator = ({ partnerId }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    // Kick off the lazy script load only when this section is mounted
    // (i.e. only when the user expands the toggle).
    loadSafetyWingScript().catch(() => { /* silent - fallback CTA still works */ });
  }, []);

  return (
    <div
      className="mt-4 mb-5 bg-[#F9F6EF] border border-[#EFE6CF] rounded-xl p-4"
      data-testid={`partner-card-safetywing-widget-${partnerId}`}
    >
      <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-3">
        SafetyWing Price Calculator
      </p>
      <div ref={containerRef}>
        {/* SafetyWing's script targets this exact id. Keep the markup as-is. */}
        <div id="safetywing-price-widget" data-scale="1.0" />
      </div>
    </div>
  );
};

const PartnerCard = ({ p }) => {
  const safetyWing = isSafetyWingPartner(p);
  const [calcOpen, setCalcOpen] = useState(false);
  const ctaHref = safetyWing ? SAFETYWING_AFFILIATE_URL : p.website_url;

  return (
    <article
      className="bg-white border border-[#EFE6CF] rounded-xl overflow-hidden flex flex-col"
      data-testid={`partner-card-${p.id}`}
    >
      <div className="px-5 py-4 border-b border-[#EFE6CF] bg-[#F9F6EF]">
        <div className="flex items-center justify-between mb-1 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {p.logo_url ? (
              <img
                src={p.logo_url}
                alt=""
                className="w-10 h-10 object-contain rounded bg-white p-1 ring-1 ring-[#EFE6CF] shrink-0"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className="w-10 h-10 rounded bg-[#F4ECD8] flex items-center justify-center shrink-0">
                <Handshake className="w-4 h-4 text-[#1A5C6E]" />
              </div>
            )}
            <h3
              className="font-display italic text-2xl text-[#0A1F2E] truncate"
              data-testid={`partner-card-name-${p.id}`}
            >
              {p.name}
            </h3>
          </div>
          <Sparkles className="w-4 h-4 text-[#E8A830] shrink-0" />
        </div>
        <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/65">
          {p.category}
        </p>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <p className="font-body text-sm text-[#0A1F2E]/80 leading-relaxed mb-4">
          {p.description}
        </p>

        {safetyWing && (
          <>
            <button
              type="button"
              onClick={() => setCalcOpen((v) => !v)}
              aria-expanded={calcOpen}
              aria-controls={`partner-card-safetywing-widget-${p.id}`}
              className="self-start inline-flex items-center gap-1.5 text-[#1A5C6E] hover:text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.14em] font-semibold transition-colors"
              data-testid={`partner-card-safetywing-toggle-${p.id}`}
            >
              <Calculator className="w-3.5 h-3.5" />
              {calcOpen ? 'Hide price calculator' : 'Calculate your price'}
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${calcOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {calcOpen && <SafetyWingPriceCalculator partnerId={p.id} />}
          </>
        )}

        <div className="flex-1" />

        <a
          href={ctaHref}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={() => trackDiscountClicked({ partner: p.id })}
          className="mt-auto inline-flex items-center justify-center gap-2 bg-[#E8A830] hover:bg-[#d59824] text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.16em] font-semibold py-3 px-5 rounded-full transition"
          data-testid={`partner-card-cta-${p.id}`}
        >
          Get Deal <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </article>
  );
};

// iter 144 - vetted_professional partner card. Different visual treatment
// from affiliate cards: VETTED badge, languages/cities row, no external
// link, member-only "Contact via Mundalo" CTA.
const VettedPartnerCard = ({ p }) => {
  const { user } = useAuth();
  const isAuthed = !!user;

  const onContactClick = () => {
    try {
      trackEvent('partner_contact_initiated', {
        partner_id: p.id, partner_category: p.category,
      });
    } catch (_) { /* noop */ }
  };

  // iter 144g - the "Contact" button on the card now routes to the full
  // profile page first. The profile page has its own "Contact via Mundalo"
  // CTA that routes to /contact.
  const signinReturn = `/start?return=${encodeURIComponent(`/portal/partners/${p.id}`)}`;

  return (
    <article
      className="relative bg-white border border-[#EFE6CF] rounded-xl overflow-hidden flex flex-col"
      data-testid={`partner-card-vetted-${p.id}`}
    >
      <span
        className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 bg-[#2CA8A0]/12 text-[#1A5C6E] font-mono uppercase text-[9px] tracking-[0.2em] font-semibold rounded"
        data-testid={`vetted-badge-${p.id}`}
      >
        <ShieldCheck className="w-3 h-3" /> Vetted
      </span>
      <div className="px-5 py-5 border-b border-[#EFE6CF] bg-[#F9F6EF] flex items-start gap-3">
        {p.photo_url ? (
          <img
            src={p.photo_url}
            alt={p.name}
            className="w-12 h-12 rounded-full object-cover border border-[#EFE6CF] shrink-0"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-[#F4ECD8] flex items-center justify-center shrink-0 text-[#1A5C6E] font-mono text-[10px] uppercase tracking-wider">
            {(p.category || 'PRO').slice(0, 3)}
          </div>
        )}
        <div className="min-w-0 flex-1 pr-12">
          <h3
            className="font-display italic text-xl text-[#0A1F2E] leading-tight"
            data-testid={`partner-card-name-${p.id}`}
          >
            {p.name}
          </h3>
          <p className="font-mono uppercase text-[10px] tracking-[0.16em] text-[#E8A830] mt-1">
            {p.category}
          </p>
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col gap-3">
        {Array.isArray(p.cities) && p.cities.length > 0 && (
          <p className="flex items-center gap-1.5 text-xs text-[#5A6B73]">
            <MapPin className="w-3 h-3" />
            {p.cities.join(' · ')}
          </p>
        )}
        {Array.isArray(p.languages) && p.languages.length > 0 && (
          <p className="flex items-center gap-1.5 text-xs text-[#5A6B73]">
            <Languages className="w-3 h-3" />
            {p.languages.join(' · ')}
          </p>
        )}
        <p className="font-body text-sm text-[#0A1F2E]/80 leading-relaxed line-clamp-3">
          {p.description}
        </p>
        <div className="flex-1" />
        {isAuthed ? (
          <Link
            to={`/portal/partners/${p.id}`}
            onClick={onContactClick}
            className="mt-auto inline-flex items-center justify-center gap-2 bg-[#E8A830] hover:bg-[#d59824] text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.16em] font-semibold py-3 px-5 rounded-full transition"
            data-testid={`partner-card-contact-${p.id}`}
          >
            <MessageCircle className="w-3.5 h-3.5" /> View Profile
          </Link>
        ) : (
          <Link
            to={signinReturn}
            className="mt-auto inline-flex items-center justify-center gap-2 border border-[#5A6B73] hover:bg-[#F4ECD8] text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.16em] font-semibold py-3 px-5 rounded-full transition"
            data-testid={`partner-card-signin-${p.id}`}
          >
            Sign in to view this partner
          </Link>
        )}
      </div>
    </article>
  );
};

const ComingSoonBanner = () => (
  <section
    className="bg-[#F4ECD8] border border-[#EFE6CF] rounded-xl p-6 md:p-8"
    data-testid="partners-more-coming"
  >
    <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
      More partners coming soon
    </p>
    <h3 className="font-display italic text-2xl md:text-3xl text-[#0A1F2E] mb-3 leading-tight">
      We only add partners we&apos;d recommend to a friend.
    </h3>
    <p className="font-body text-sm md:text-base text-[#0A1F2E]/75 max-w-2xl">
      Long-stay housing, healthcare networks, international movers, pet
      relocation, bilingual schools, storage - each in active vetting. We
      won&apos;t list anything until we have a real affiliate agreement and a
      Mundalo-specific member benefit. Want a category prioritized?{' '}
      <a
        href="mailto:hello@mundalo.com?subject=Partner%20suggestion"
        className="underline decoration-[#E8A830] underline-offset-4 hover:text-[#0A1F2E]"
      >
        Email us
      </a>
      .
    </p>
  </section>
);

export default function PortalDiscounts() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState('');
  // iter 147 - Accordion state. One category expanded at a time; null
  // means all collapsed. We default-open the first category that has
  // partners after filters apply, so the page doesn't read as empty.
  const [openCategory, setOpenCategory] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/partners`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        setPartners(Array.isArray(d.partners) ? d.partners : []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const vetted = useMemo(
    () => partners.filter((p) => p.partner_type === 'vetted_professional'),
    [partners],
  );
  const affiliates = useMemo(
    () => partners.filter((p) => (p.partner_type || 'institutional_affiliate') === 'institutional_affiliate'),
    [partners],
  );

  // Unique cities across vetted partners - fuels the city dropdown.
  const cityOptions = useMemo(() => {
    const s = new Set();
    vetted.forEach((p) => (p.cities || []).forEach((c) => s.add(c)));
    return Array.from(s).sort();
  }, [vetted]);

  // Apply city filter to vetted partners.
  const visibleVetted = useMemo(() => {
    if (!cityFilter) return vetted;
    return vetted.filter((p) => (p.cities || []).includes(cityFilter));
  }, [vetted, cityFilter]);
  // iter 147 - Affiliates section stays as-is (just below the accordion).
  const visibleAffiliates = affiliates;

  // iter 147 - Category accordion config. Order is fixed per spec.
  // `match` accepts the partner's raw `category` string (case-insensitive
  // substring match) so admin-entered values like "Legal Services" or
  // "Banking & Finance" still group cleanly.
  const CATEGORY_DEFS = useMemo(() => ([
    { id: 'legal',      label: 'Legal',      icon: Scale,           match: ['legal'] },
    { id: 'financial',  label: 'Financial',  icon: Banknote,        match: ['financ', 'bank', 'tax', 'account'] },
    { id: 'healthcare', label: 'Healthcare', icon: HeartPulse,      match: ['health', 'medic', 'dental', 'doctor', 'wellness'] },
    { id: 'insurance',  label: 'Insurance',  icon: Umbrella,        match: ['insur'] },
    { id: 'technology', label: 'Technology', icon: Cpu,             match: ['tech', 'software', 'it ', 'internet', 'telecom'] },
    { id: 'other',      label: 'Other',      icon: MoreHorizontal,  match: [] },
  ]), []);

  // Bucket every visible vetted partner into exactly one accordion group.
  const groupedByCategory = useMemo(() => {
    const groups = Object.fromEntries(CATEGORY_DEFS.map((c) => [c.id, []]));
    visibleVetted.forEach((p) => {
      const raw = (p.category || '').toLowerCase();
      const match = CATEGORY_DEFS.find((c) => c.id !== 'other' && c.match.some((m) => raw.includes(m)));
      groups[match ? match.id : 'other'].push(p);
    });
    return groups;
  }, [visibleVetted, CATEGORY_DEFS]);

  // Auto-open the first non-empty category whenever the buckets change.
  useEffect(() => {
    const firstWith = CATEGORY_DEFS.find((c) => (groupedByCategory[c.id] || []).length > 0);
    setOpenCategory((current) => {
      // If current is still populated, keep it. Otherwise pick the first.
      if (current && (groupedByCategory[current] || []).length > 0) return current;
      return firstWith ? firstWith.id : null;
    });
  }, [groupedByCategory, CATEGORY_DEFS]);

  return (
    <PortalLayout activeTab="partners">
      <SEO
        title="Partners - Mundalo"
        description="Mundalo-vetted Mexico professionals and affiliate partners for your move."
        path="/portal/partners"
      />

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="portal-partners-page">
        <header className="mb-6">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
            The Life · Partners
          </p>
          <h1
            className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3"
            data-testid="partners-headline"
          >
            Mundalo-vetted services for your move.
          </h1>
          <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
            Vetted Mexico professionals you can contact directly through
            Mundalo - plus the affiliate partners we&apos;ve negotiated rates with.
          </p>
        </header>

        {/* iter 147 - City filter row only (type tabs removed). */}
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="partners-filter-row">
          {cityOptions.length > 0 && (
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-[#EFE6CF] text-sm text-[#0A1F2E] rounded-full focus:outline-none focus:border-[#2CA8A0]"
              data-testid="partners-city-filter"
            >
              <option value="">All cities</option>
              {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>

        {/* FTC affiliate disclosure - legally required */}
        <div
          className="bg-[#FFF9EC] border-l-4 border-[#E8A830] text-[#0A1F2E]/85 px-4 py-3 mb-8 rounded-r font-body text-sm"
          data-testid="ftc-affiliate-disclosure"
          role="note"
        >
          <span className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] block mb-1">
            Affiliate disclosure
          </span>
          {FTC_DISCLOSURE}
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-[#0A1F2E]/50">Loading partners…</div>
        ) : (
          <>
            {/* iter 147 - Vetted Mexico professionals as a category-first
                accordion. One section expandable at a time. City filter
                (above) applies to the partners shown inside each row. */}
            {visibleVetted.length > 0 && (
              <section className="mb-10" data-testid="partners-vetted-section">
                <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-5">
                  Vetted Mexico professionals
                </h2>
                <div
                  className="bg-white border border-[#EFE6CF] rounded-2xl overflow-hidden"
                  data-testid="partners-accordion"
                >
                  {CATEGORY_DEFS.map((cat, idx) => {
                    const list = groupedByCategory[cat.id] || [];
                    const isOpen = openCategory === cat.id;
                    const Icon = cat.icon;
                    return (
                      <div
                        key={cat.id}
                        className={idx === 0 ? '' : 'border-t border-[#EFE6CF]'}
                        data-testid={`partners-accordion-section-${cat.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => setOpenCategory(isOpen ? null : cat.id)}
                          disabled={list.length === 0}
                          data-testid={`partners-accordion-header-${cat.id}`}
                          aria-expanded={isOpen}
                          aria-controls={`partners-accordion-panel-${cat.id}`}
                          className={`w-full flex items-center gap-3 px-5 md:px-6 py-4 text-left transition ${
                            list.length === 0
                              ? 'opacity-40 cursor-not-allowed'
                              : 'hover:bg-[#F9F6EF]'
                          }`}
                        >
                          <span className="w-8 h-8 bg-[#F4ECD8] rounded-lg flex items-center justify-center text-[#1A5C6E] flex-none">
                            <Icon className="w-4 h-4" />
                          </span>
                          <span className="font-display italic text-xl text-[#0A1F2E] flex-1">{cat.label}</span>
                          <span
                            className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-[#F4ECD8] text-[#1A5C6E] font-mono text-[11px] tracking-[0.1em]"
                            data-testid={`partners-accordion-count-${cat.id}`}
                          >
                            {list.length}
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 text-[#0A1F2E]/55 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                            aria-hidden="true"
                          />
                        </button>
                        {isOpen && list.length > 0 && (
                          <div
                            id={`partners-accordion-panel-${cat.id}`}
                            className="border-t border-[#EFE6CF] bg-[#F9F6EF]/50"
                            data-testid={`partners-accordion-panel-${cat.id}`}
                          >
                            <ul className="divide-y divide-[#EFE6CF]">
                              {list.map((p) => {
                                const cityLabel = (p.cities && p.cities[0]) || '';
                                return (
                                  <li
                                    key={p.id}
                                    className="flex items-center gap-4 px-5 md:px-6 py-3.5"
                                    data-testid={`partners-row-${p.id}`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[#0A1F2E] font-medium truncate">{p.name}</p>
                                      {cityLabel && (
                                        <p className="font-mono uppercase text-[10px] tracking-[0.16em] text-[#1A5C6E] mt-0.5">
                                          {cityLabel}
                                        </p>
                                      )}
                                    </div>
                                    <Link
                                      to={`/portal/partners/${p.id}`}
                                      data-testid={`partners-row-view-${p.id}`}
                                      className="flex-none inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0A1F2E] text-white text-xs font-mono uppercase tracking-[0.14em] hover:bg-[#163448] transition"
                                    >
                                      View Profile <ArrowRight className="w-3 h-3" />
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {visibleAffiliates.length > 0 && (
              <section className="mb-12" data-testid="partners-featured">
                <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-5">
                  Affiliate partners
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {visibleAffiliates.map((p) => <PartnerCard key={p.id} p={p} />)}
                </div>
              </section>
            )}

            {!visibleVetted.length && !visibleAffiliates.length && (
              <div className="py-10 text-center text-sm text-[#0A1F2E]/65 bg-white border border-[#EFE6CF] rounded-xl">
                No partners match the current filter.
              </div>
            )}
          </>
        )}

        <ComingSoonBanner />
      </div>
    </PortalLayout>
  );
}
